import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";

var _TYPOGRAPHY_TO_COMPON;

import React from 'react';
import classNames from 'classnames';
import capitalize from 'lodash/capitalize';
import { object, string, oneOf, bool, elementType } from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import GutterBottom from '../helpers/gutter-bottom';

var styles = function styles(_ref) {
  var unityTypography = _ref.unityTypography,
      palette = _ref.palette;
  return {
    root: {
      margin: 0
    },
    headline: unityTypography.headline,
    titleBig: unityTypography.titleBig,
    titleSmall: unityTypography.titleSmall,
    body: unityTypography.body,
    bodyLight: unityTypography.bodyLight,
    bodyStrong: unityTypography.bodyStrong,
    caption: unityTypography.caption,
    overline: unityTypography.overline,
    alignLeft: {
      textAlign: 'left'
    },
    alignCenter: {
      textAlign: 'center'
    },
    alignRight: {
      textAlign: 'right'
    },
    alignJustify: {
      textAlign: 'justify'
    },
    noWrap: {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    },
    colorInherit: {
      color: 'inherit'
    },
    colorError: {
      color: palette.error.main
    },
    colorPrimary: {
      color: palette.primary.main
    },
    colorSecondary: {
      color: palette.secondary.main
    },
    wordBreakAll: {
      wordBreak: 'break-all'
    },
    wordBreakWord: {
      wordBreak: 'break-word'
    },
    displayInline: {
      display: 'inline'
    },
    displayBlock: {
      display: 'block'
    }
  };
};

var DEFAULT = 'default';
var INITIAL = 'initial';
export var TYPOGRAPHY_VARIANTS = {
  INHERIT: 'inherit',
  HEADLINE: 'headline',
  TITLE_BIG: 'titleBig',
  TITLE_SMALL: 'titleSmall',
  BODY: 'body',
  BODY_STRONG: 'bodyStrong',
  BODY_LIGHT: 'bodyLight',
  CAPTION: 'caption',
  OVERLINE: 'overline'
};
var VARIANTS = Object.values(TYPOGRAPHY_VARIANTS);
export var TYPOGRAPHY_TO_COMPONENT_MAPPING = (_TYPOGRAPHY_TO_COMPON = {}, _defineProperty(_TYPOGRAPHY_TO_COMPON, TYPOGRAPHY_VARIANTS.HEADLINE, 'h2'), _defineProperty(_TYPOGRAPHY_TO_COMPON, TYPOGRAPHY_VARIANTS.TITLE_BIG, 'h3'), _defineProperty(_TYPOGRAPHY_TO_COMPON, TYPOGRAPHY_VARIANTS.TITLE_SMALL, 'h4'), _defineProperty(_TYPOGRAPHY_TO_COMPON, TYPOGRAPHY_VARIANTS.BODY, 'p'), _defineProperty(_TYPOGRAPHY_TO_COMPON, TYPOGRAPHY_VARIANTS.BODY_STRONG, 'p'), _defineProperty(_TYPOGRAPHY_TO_COMPON, TYPOGRAPHY_VARIANTS.BODY_LIGHT, 'p'), _defineProperty(_TYPOGRAPHY_TO_COMPON, TYPOGRAPHY_VARIANTS.CAPTION, 'p'), _defineProperty(_TYPOGRAPHY_TO_COMPON, TYPOGRAPHY_VARIANTS.OVERLINE, 'p'), _defineProperty(_TYPOGRAPHY_TO_COMPON, TYPOGRAPHY_VARIANTS.INHERIT, 'p'), _TYPOGRAPHY_TO_COMPON); // eslint-disable-next-line prefer-arrow-callback

export var Typography = React.forwardRef(function Typography(props, ref) {
  var _classNames;

  var align = props.align,
      color = props.color,
      classes = props.classes,
      className = props.className,
      display = props.display,
      gutterBottom = props.gutterBottom,
      noWrap = props.noWrap,
      variant = props.variant,
      wordBreak = props.wordBreak,
      component = props.component,
      rest = _objectWithoutProperties(props, ["align", "color", "classes", "className", "display", "gutterBottom", "noWrap", "variant", "wordBreak", "component"]);

  var isAllowedVariant = VARIANTS.includes(variant);

  if (!isAllowedVariant) {
    console.error("You are using an unsupported variant ".concat(variant, ", we are remapping our variants to material UI variants, use our variants instead."));
  }

  var ComponentToUse = component || TYPOGRAPHY_TO_COMPONENT_MAPPING[variant];
  var typographyClasses = classNames(className, classes.root, (_classNames = {}, _defineProperty(_classNames, classes.noWrap, noWrap), _defineProperty(_classNames, classes.gutterBottom, gutterBottom), _defineProperty(_classNames, classes[variant], variant !== VARIANTS.INHERIT), _defineProperty(_classNames, classes["wordBreak".concat(capitalize(wordBreak))], wordBreak !== DEFAULT), _defineProperty(_classNames, classes["display".concat(capitalize(display))], display !== INITIAL), _defineProperty(_classNames, classes["color".concat(capitalize(color))], color !== DEFAULT), _defineProperty(_classNames, classes["align".concat(capitalize(align))], align !== 'inherit'), _classNames));
  return React.createElement(GutterBottom, {
    gutterBottom: gutterBottom
  }, React.createElement(ComponentToUse, _extends({}, rest, {
    ref: ref,
    className: typographyClasses
  })));
});
process.env.NODE_ENV !== "production" ? Typography.propTypes = {
  classes: object.isRequired,
  align: oneOf(['left', 'center', 'right', 'justify', 'inherit']),
  className: string,
  color: oneOf([DEFAULT, 'inherit', 'primary', 'secondary', 'error']),
  component: elementType,
  display: oneOf(['initial', 'block', 'inline']),
  gutterBottom: oneOf(['small', 'medium', 'large']),
  noWrap: bool,
  variant: oneOf([TYPOGRAPHY_VARIANTS.INHERIT, TYPOGRAPHY_VARIANTS.HEADLINE, TYPOGRAPHY_VARIANTS.TITLE_BIG, TYPOGRAPHY_VARIANTS.TITLE_SMALL, TYPOGRAPHY_VARIANTS.BODY, TYPOGRAPHY_VARIANTS.BODY_STRONG, TYPOGRAPHY_VARIANTS.BODY_LIGHT, TYPOGRAPHY_VARIANTS.CAPTION, TYPOGRAPHY_VARIANTS.OVERLINE]),
  wordBreak: oneOf([DEFAULT, 'all', 'word'])
} : void 0;
Typography.defaultProps = {
  align: 'inherit',
  className: null,
  display: 'initial',
  noWrap: false,
  color: DEFAULT,
  component: null,
  variant: TYPOGRAPHY_VARIANTS.BODY,
  wordBreak: DEFAULT,
  gutterBottom: null
};
export default withStyles(styles, {
  name: 'Typography'
})(Typography);